import { useCallback, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { DeskViewContext } from "./DeskViewContext";
import type { DeskViewValue } from "./DeskViewContext";
import { useDeskTab } from "../hooks/useDeskTab";

/**
 * Owns the desk's view state above both the masthead and the desk itself (`APP-017`).
 *
 * Rendered by `App`, so it is mounted for every session state. That costs a `popstate`
 * listener while nobody is signed in and nothing else: no read is issued here, and no
 * surface but the desk renders anything derived from it.
 */
export function DeskViewProvider({ children }: { children: ReactNode }) {
  const { tab, goTo } = useDeskTab();
  const [submitOpen, setSubmitOpen] = useState(false);

  const openSubmit = useCallback(() => {
    // Submission belongs to the deals view. Pressing the action from the auction takes the
    // founder to the form rather than opening it somewhere they cannot see it.
    goTo("deals");
    setSubmitOpen(true);
  }, [goTo]);

  const closeSubmit = useCallback(() => {
    setSubmitOpen(false);
  }, []);

  const value = useMemo<DeskViewValue>(
    () => ({ tab, goTo, submitOpen, openSubmit, closeSubmit }),
    [tab, goTo, submitOpen, openSubmit, closeSubmit],
  );

  return <DeskViewContext value={value}>{children}</DeskViewContext>;
}
